import { 
  UserAuditEntry, 
  AuditSearchFilters, 
  AuditSearchResult, 
  AuditStatistics,
  AuditEventType,
  AuditSeverity,
  AuditStatus
} from '@/lib/types/audit';
import { generateNumericId } from '@/lib/utils';
import { serviceLogger } from '@/lib/utils/logger';

const SAMPLE_DATA_DELAY = 100;
const logger = serviceLogger('AuditManager');

// In-memory storage for sample data mode
let localAuditEntries: UserAuditEntry[] = [];

type CreateAuditEntry = Omit<UserAuditEntry, 'id' | 'timestamp'>;

/**
 * Simplified Audit Manager
 * Handles audit logging and retrieval with streamlined operations
 */
export const auditManager = {
  /**
   * Search audit entries with filters
   */
  async searchAuditEntries(filters: AuditSearchFilters = {}): Promise<AuditSearchResult> {
    await new Promise(resolve => setTimeout(resolve, SAMPLE_DATA_DELAY));
    
    const maskedFilters = {
      ...filters,
      userEmail: filters.userEmail ? `***${filters.userEmail.split('@')[0].slice(-2)}@***` : undefined,
      actorEmail: filters.actorEmail ? `***${filters.actorEmail.split('@')[0].slice(-2)}@***` : undefined
    };
    logger.debug('Searching audit entries with filters:', maskedFilters);
    
    // Ensure data is initialized
    if (localAuditEntries.length === 0) {
      this.initializeSampleData();
    }

    let filteredEntries = [...localAuditEntries];

    // Apply filters
    if (filters.eventTypes?.length) {
      filteredEntries = filteredEntries.filter(entry => 
        filters.eventTypes!.includes(entry.eventType)
      );
    }

    if (filters.severities?.length) {
      filteredEntries = filteredEntries.filter(entry => 
        filters.severities!.includes(entry.severity)
      );
    }

    if (filters.statuses?.length) {
      filteredEntries = filteredEntries.filter(entry => 
        filters.statuses!.includes(entry.status)
      );
    }

    if (filters.userEmail) {
      filteredEntries = filteredEntries.filter(entry => 
        entry.userEmail?.toLowerCase().includes(filters.userEmail!.toLowerCase())
      );
    }

    if (filters.actorEmail) {
      filteredEntries = filteredEntries.filter(entry => 
        entry.actorEmail?.toLowerCase().includes(filters.actorEmail!.toLowerCase())
      );
    }

    if (filters.startDate) {
      filteredEntries = filteredEntries.filter(entry => 
        entry.timestamp >= filters.startDate!
      );
    }

    if (filters.endDate) {
      filteredEntries = filteredEntries.filter(entry => 
        entry.timestamp <= filters.endDate!
      );
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filteredEntries = filteredEntries.filter(entry => 
        entry.description?.toLowerCase().includes(query) ||
        entry.details?.toLowerCase().includes(query) ||
        entry.userName?.toLowerCase().includes(query) ||
        entry.userEmail?.toLowerCase().includes(query)
      );
    }

    // Sort entries
    const sortBy = filters.sortBy || 'timestamp';
    const sortOrder = filters.sortOrder || 'desc';
    
    filteredEntries.sort((a, b) => {
      let aVal: number, bVal: number;
      
      switch (sortBy) {
        case 'timestamp':
          aVal = a.timestamp.getTime();
          bVal = b.timestamp.getTime();
          break;
        case 'severity':
          const severityWeights = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
          aVal = severityWeights[a.severity] || 0;
          bVal = severityWeights[b.severity] || 0;
          break;
        default:
          aVal = a.timestamp.getTime();
          bVal = b.timestamp.getTime();
      }
      
      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    // Apply pagination
    const page = filters.page || 1;
    const limit = filters.limit || 50;
    const offset = (page - 1) * limit;
    const paginatedEntries = filteredEntries.slice(offset, offset + limit);

    const totalPages = Math.ceil(filteredEntries.length / limit);

    return {
      entries: paginatedEntries,
      totalCount: filteredEntries.length,
      pageCount: totalPages,
      currentPage: page,
      hasNext: page < totalPages,
      hasPrevious: page > 1,
    };
  },

  /**
   * Get audit statistics
   */
  async getAuditStatistics(): Promise<AuditStatistics> {
    await new Promise(resolve => setTimeout(resolve, SAMPLE_DATA_DELAY));
    
    logger.debug('Getting audit statistics');
    
    // Ensure data is initialized
    if (localAuditEntries.length === 0) {
      this.initializeSampleData();
    }

    const now = new Date();
    const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // Count by fields
    const eventsByType: Record<AuditEventType, number> = {} as Record<AuditEventType, number>;
    const eventsBySeverity: Record<AuditSeverity, number> = {} as Record<AuditSeverity, number>;
    const eventsByStatus: Record<AuditStatus, number> = {} as Record<AuditStatus, number>;

    localAuditEntries.forEach(entry => {
      eventsByType[entry.eventType] = (eventsByType[entry.eventType] || 0) + 1;
      eventsBySeverity[entry.severity] = (eventsBySeverity[entry.severity] || 0) + 1;
      eventsByStatus[entry.status] = (eventsByStatus[entry.status] || 0) + 1;
    });

    // Get top users (simplified)
    const userCounts: Record<string, { userId: string; userEmail: string; count: number; lastActivity: Date }> = {};
    localAuditEntries.forEach(entry => {
      if (entry.userId) {
        if (!userCounts[entry.userId]) {
          userCounts[entry.userId] = {
            userId: entry.userId,
            userEmail: entry.userEmail || '',
            count: 0,
            lastActivity: entry.timestamp
          };
        }
        userCounts[entry.userId].count++;
        if (entry.timestamp > userCounts[entry.userId].lastActivity) {
          userCounts[entry.userId].lastActivity = entry.timestamp;
        }
      }
    });

    const topUsers = Object.values(userCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map(user => ({
        userId: user.userId,
        userEmail: user.userEmail,
        eventCount: user.count,
        lastActivity: user.lastActivity
      }));

    // Get top actors (simplified)
    const actorCounts: Record<string, { actorId: string; actorEmail: string; count: number; lastAction: Date }> = {};
    localAuditEntries.forEach(entry => {
      if (entry.actorId) {
        if (!actorCounts[entry.actorId]) {
          actorCounts[entry.actorId] = {
            actorId: entry.actorId,
            actorEmail: entry.actorEmail || '',
            count: 0,
            lastAction: entry.timestamp
          };
        }
        actorCounts[entry.actorId].count++;
        if (entry.timestamp > actorCounts[entry.actorId].lastAction) {
          actorCounts[entry.actorId].lastAction = entry.timestamp;
        }
      }
    });

    const topActors = Object.values(actorCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map(actor => ({
        actorId: actor.actorId,
        actorEmail: actor.actorEmail,
        actionCount: actor.count,
        lastAction: actor.lastAction
      }));

    // Generate events over time (last 30 days)
    const eventsOverTime: Array<{ date: Date; count: number; criticalCount: number }> = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      
      const dayEvents = localAuditEntries.filter(entry => 
        entry.timestamp >= date && entry.timestamp < nextDate
      );
      
      eventsOverTime.push({
        date,
        count: dayEvents.length,
        criticalCount: dayEvents.filter(e => e.severity === 'CRITICAL').length
      });
    }

    return {
      totalEvents: localAuditEntries.length,
      todayEvents: localAuditEntries.filter(e => e.timestamp >= last24h).length,
      failedEvents: localAuditEntries.filter(e => e.status === 'FAILED').length,
      criticalEvents: localAuditEntries.filter(e => e.severity === 'CRITICAL').length,
      
      eventsByType,
      eventsBySeverity,
      eventsByStatus,
      
      topUsers,
      topActors,
      
      recentActivity: localAuditEntries
        .filter(e => e.timestamp >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000))
        .slice(0, 10),
      eventsOverTime,
    };
  },

  /**
   * Get audit entry by ID
   */
  async getAuditEntryById(id: string): Promise<UserAuditEntry | null> {
    await new Promise(resolve => setTimeout(resolve, SAMPLE_DATA_DELAY));
    
    logger.debug('Getting audit entry by ID:', id);
    
    // Ensure data is initialized
    if (localAuditEntries.length === 0) {
      this.initializeSampleData();
    }

    return localAuditEntries.find(entry => entry.id === id) || null;
  },

  /**
   * Add new audit entry
   */
  async addAuditEntry(entryData: CreateAuditEntry): Promise<UserAuditEntry> {
    await new Promise(resolve => setTimeout(resolve, SAMPLE_DATA_DELAY));
    
    logger.debug('Adding audit entry:', entryData.eventType);

    const entry: UserAuditEntry = {
      id: generateNumericId(10),
      timestamp: new Date(),
      ...entryData,
    };

    localAuditEntries.unshift(entry); // Add to beginning for recent first
    return entry;
  },

  /**
   * Get recent audit entries
   */
  async getRecentAuditEntries(limit: number = 10): Promise<UserAuditEntry[]> {
    await new Promise(resolve => setTimeout(resolve, SAMPLE_DATA_DELAY));
    
    logger.debug('Getting recent audit entries, limit:', limit);
    
    // Ensure data is initialized
    if (localAuditEntries.length === 0) {
      this.initializeSampleData();
    }

    return localAuditEntries
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  },

  /**
   * Refresh sample data (for demo purposes)
   */
  async refreshSampleData(): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, SAMPLE_DATA_DELAY));
    
    logger.info('Refreshing audit sample data');
    this.initializeSampleData();
  },

  /**
   * Get audit summary for dashboard
   */
  async getAuditSummary(): Promise<{
    recentEvents: UserAuditEntry[];
    criticalAlerts: UserAuditEntry[];
    dailyTrend: Array<{ date: string; count: number; }>;
    statusDistribution: Array<{ status: AuditStatus; count: number; }>;
  }> {
    await new Promise(resolve => setTimeout(resolve, SAMPLE_DATA_DELAY));
    
    logger.debug('Getting audit summary');
    
    // Ensure data is initialized
    if (localAuditEntries.length === 0) {
      this.initializeSampleData();
    }

    const last7Days = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

    // Generate daily trend
    const dailyTrend: Array<{ date: string; count: number; }> = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      
      const count = localAuditEntries.filter(e => 
        e.timestamp >= dayStart && e.timestamp <= dayEnd
      ).length;
      
      dailyTrend.push({ date: dateStr, count });
    }

    // Get status distribution
    const statusCounts: Partial<Record<AuditStatus, number>> = {};
    localAuditEntries.forEach(entry => {
      statusCounts[entry.status] = (statusCounts[entry.status] || 0) + 1;
    });

    const statusDistribution = Object.entries(statusCounts).map(([status, count]) => ({
      status: status as AuditStatus,
      count
    }));

    return {
      recentEvents: localAuditEntries
        .filter(e => e.timestamp >= last7Days)
        .slice(0, 10),
      
      criticalAlerts: localAuditEntries
        .filter(e => e.severity === 'CRITICAL')
        .slice(0, 5),
      
      dailyTrend,
      statusDistribution,
    };
  },

  /**
   * Initialize sample data
   */
  initializeSampleData(): void {
    logger.info('Initializing audit sample data');
    localAuditEntries = this.generateSampleAuditData();
  },

  /**
   * Generate sample audit data
   */
  generateSampleAuditData(): UserAuditEntry[] {
    const events: UserAuditEntry[] = [];
    const now = new Date();
    
    // Sample users and actors
    const sampleUsers = [
      { id: '1001', email: 'john.doe@example.com', name: 'John Doe' },
      { id: '1002', email: 'jane.smith@example.com', name: 'Jane Smith' },
      { id: '1003', email: 'mike.wilson@example.com', name: 'Mike Wilson' },
      { id: '1004', email: 'sarah.johnson@example.com', name: 'Sarah Johnson' },
      { id: '1005', email: 'david.brown@example.com', name: 'David Brown' },
    ];

    const sampleActors = [
      { id: 'admin001', email: 'admin@courtly25.com', name: 'Platform Admin', role: 'Super Admin' },
      { id: 'org001', email: 'org.admin@sportscenter.com', name: 'Org Admin', role: 'Organization Admin' },
      { id: 'club001', email: 'club.manager@tennisclub.com', name: 'Club Manager', role: 'Club Manager' },
      { id: 'system', email: 'system@courtly25.com', name: 'System', role: 'System' },
    ];

    const eventTemplates = [
      { eventType: 'USER_CREATED' as AuditEventType, severity: 'MEDIUM' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'New user account created', details: 'User registration completed successfully' },
      { eventType: 'USER_UPDATED' as AuditEventType, severity: 'LOW' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'User profile updated', details: 'Profile information modified by user' },
      { eventType: 'USER_SUSPENDED' as AuditEventType, severity: 'HIGH' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'User account suspended', details: 'Account suspended due to policy violation' },
      { eventType: 'LOGIN_SUCCESS' as AuditEventType, severity: 'LOW' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'Successful user login', details: 'User authenticated successfully' },
      { eventType: 'LOGIN_FAILED' as AuditEventType, severity: 'MEDIUM' as AuditSeverity, status: 'FAILED' as AuditStatus, description: 'Failed login attempt', details: 'Authentication failed - invalid credentials' },
      { eventType: 'PASSWORD_CHANGED' as AuditEventType, severity: 'MEDIUM' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'Password changed', details: 'User password updated successfully' },
      { eventType: 'ROLE_ASSIGNED' as AuditEventType, severity: 'MEDIUM' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'Role assigned to user', details: 'New role granted with specific permissions' },
      { eventType: 'PERMISSION_GRANTED' as AuditEventType, severity: 'MEDIUM' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'Permission granted', details: 'Additional permission granted to user' },
      { eventType: 'SUSPICIOUS_ACTIVITY' as AuditEventType, severity: 'CRITICAL' as AuditSeverity, status: 'WARNING' as AuditStatus, description: 'Suspicious activity detected', details: 'Unusual access pattern detected' },
      { eventType: 'IP_BLOCKED' as AuditEventType, severity: 'HIGH' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'IP address blocked', details: 'IP blocked due to repeated failed attempts' },
      { eventType: 'DATA_EXPORTED' as AuditEventType, severity: 'MEDIUM' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'Data export performed', details: 'User data exported to CSV format' },
      { eventType: 'SENSITIVE_DATA_ACCESSED' as AuditEventType, severity: 'HIGH' as AuditSeverity, status: 'SUCCESS' as AuditStatus, description: 'Sensitive data accessed', details: 'Access to PII data logged' }
    ];

    const ipAddresses = ['192.168.1.100', '10.0.0.50', '172.16.0.25', '203.0.113.42', '198.51.100.33'];
    const userAgents = [
      'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X)',
      'CourtlyApp/2.1.0 (iOS 15.0)',
      'CourtlyApp/2.1.0 (Android 12)'
    ];

    // Generate events for the last 30 days
    for (let i = 0; i < 150; i++) {
      const daysAgo = Math.floor(Math.random() * 30);
      const hoursAgo = Math.floor(Math.random() * 24);
      const minutesAgo = Math.floor(Math.random() * 60);
      
      const timestamp = new Date(now);
      timestamp.setDate(timestamp.getDate() - daysAgo);
      timestamp.setHours(timestamp.getHours() - hoursAgo);
      timestamp.setMinutes(timestamp.getMinutes() - minutesAgo);

      const user = sampleUsers[Math.floor(Math.random() * sampleUsers.length)];
      const actor = sampleActors[Math.floor(Math.random() * sampleActors.length)];
      const template = eventTemplates[Math.floor(Math.random() * eventTemplates.length)];

             events.push({
         id: generateNumericId(10),
         eventType: template.eventType,
         severity: template.severity,
         status: template.status,
         
         // User Information
         userId: user.id,
         userEmail: user.email,
         userName: user.name,
         
         // Actor Information
         actorId: actor.id,
         actorEmail: actor.email,
         actorName: actor.name,
         actorRole: actor.role,
         
         // Event Details
         description: template.description,
         details: template.details,
         timestamp,
         
         // Context
         context: {
           organizationId: '1001',
           ipAddress: ipAddresses[Math.floor(Math.random() * ipAddresses.length)],
           userAgent: userAgents[Math.floor(Math.random() * userAgents.length)],
           sessionId: generateNumericId(8),
           resourceType: 'users',
           resourceId: user.id,
         },
         
         // Metadata
         metadata: {
           source: 'WEB' as const,
           version: '1.0.0',
           correlationId: generateNumericId(12),
         },
       });
    }

    return events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }
}; 
